<template>
    <div class="create-bundle">
        <form ref="form" novalidate>
            <DsInputField
                v-model="bundle.name"
                type="text"
                :label="$t('campaignsPage.createBundle.name')"
                name="bundleName"
                :submitted="submitted"
                required
            />

            <DsInputField
                v-model="bundle.description"
                type="text"
                :label="$t('campaignsPage.createBundle.description')"
                :submitted="submitted"
            />
        </form>

        <div>
            <span class="title">{{ $t('campaignsPage.createBundle.selectCampaigns') }}</span>

            <div class="search-listings">
                <DsInputField
                    v-model="searchText"
                    type="text"
                    :label="$t('campaignsPage.createBundle.filterCampaigns')"
                />

                <div
                    v-if="bundle.solutionRefs.length"
                    class="bundles-selected"
                >
                    <span class="bundle-count">
                        {{ bundle.solutionRefs.length }}
                    </span>

                    {{ $t('campaignsPage.createBundle.selected') }}
                </div>
            </div>

            <div v-show="!firstLoad" class="loadingContainer">
                <span
                    v-if="loading"
                    :class="{ visible: count }"
                    class="spinner"
                />

                <div v-if="loading" class="totals">
                    <div v-if="searchText">
                        <span class="numbers">{{ $t('campaignsPage.createBundle.results', { count: listings.length, total }) }}</span>
                        {{ $tc('campaignsPage.createBundle.filtered', listings.length) }}
                    </div>

                    <div v-else>
                        <span class="numbers">{{ $t('campaignsPage.createBundle.results', { count, total }) }}</span>
                        {{ $tc('campaignsPage.createBundle.campaigns', total) }}
                    </div>
                </div>

                <div v-else class="totals">
                    <div v-if="searchText">
                        <span class="numbers">{{ $t('campaignsPage.createBundle.results', { count: listings.length, total }) }}</span>
                        {{ $tc('campaignsPage.createBundle.filtered', listings.length) }}
                    </div>

                    <div v-else>
                        <span class="numbers">{{ total }}</span>
                        {{ $tc('campaignsPage.createBundle.campaigns', total) }}
                    </div>
                </div>
            </div>
        </div>

        <div v-if="loading && firstLoad" class="placeholder">
            <DsPlaceholder
                v-for="n in 10"
                :key="n"
                class="listings-placeholder"
                :rows="placeholder"
            />
        </div>

        <div v-else class="listings-list">
            <div
                v-for="listing in listings"
                :key="listing.id"
                class="campaign"
            >
                {{ listing.name }}

                <DsButton
                    :variant="isSelected(listing) ? 'outline' : 'filled'"
                    :data-qa="isSelected(listing) ? 'remove' : 'add'"
                    dense
                    @click="toggleCampaignSelect(listing)"
                >
                    <span v-if="isSelected(listing)">
                        {{ $t('campaignsPage.createBundle.remove') }}
                    </span>

                    <span v-else>
                        {{ $t('campaignsPage.createBundle.add') }}
                    </span>
                </DsButton>
            </div>

            <div v-if="!loading && !listings.length" class="no-results">
                {{ $t('campaignsPage.createBundle.noResults') }}
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';

import debounce from 'lodash.debounce';

import CreateBundleConfirm from './CreateBundleConfirm';

export default {
    props: {
        campaigns: Array,
    },

    data() {
        return {
            placeholder: [{ height: '1.5rem', boxes: [4, 1] }],
            bundle: {
                name: null,
                description: null,
                organizationId: this.organizationId,
                solutionRefs: [],
            },
            loading: true,
            submitted: false,
            nestedModal_title: this.$t('campaignsPage.createBundle.title'),
            nestedModal_actionText: this.$t('Next'),
            nestedModal_actionMethod: 'next',
            nestedModal_actionDisabled: true,
            nestedModal_actionClasses: 'small',
            searchText: '',
            listings: [],
            dataset: [],
            total: -1,
            firstLoad: true,
        };
    },

    beforeDestroy() {
        /* istanbul ignore next */
        this.$bus.$emit('POP_NESTED_MODAL');
    },

    created() {
        /* istanbul ignore next */
        this.loadListings = debounce(this.loadListings, 500);
    },

    watch: {
        publisherCompanyIdsLoaded(value) {
            if (value) {
                this.loadListings();
            }
        },

        searchText(value) {
            this.filterListings(value);
        },

        bundle: {
            handler() {
                this.nestedModal_actionDisabled = !this.validateBundle();
            },
            deep: true,
        },
    },

    computed: {
        count() {
            return this.dataset.length || 0;
        },

        ...mapState({
            userOrganization: ({ auth }) => auth.companyProfile,
            publisherCompanyIdsLoaded({ automations }) {
                const result = automations.publisherCompanyIds.length > 0;

                if (result) {
                    this.loadListings();
                }

                return result;
            },
        }),
    },

    methods: {
        filterListings(searchText) {
            const filteredListings = this.dataset.filter(({ name }) => {
                return name.toLowerCase().includes(searchText.toLowerCase());
            });

            this.listings = filteredListings;
        },

        nestedModal_open(campaigns) {
            this.searchText = '';

            if (campaigns && campaigns.length) {
                this.bundle.solutionRefs = campaigns;
            }

            if (this.userOrganization && this.userOrganization.id) {
                this.bundle.organizationId = this.userOrganization.id;
            }
        },

        loadListings(page = 0) {
            this.loading = true;

            this.$store.dispatch('automations/LOAD_LISTINGS', {
                pageSize: 50,
                pageNumber: page,
            })
                .then(({ listings, totalListings }) => {
                    this.total = totalListings;

                    const results = listings.map(({ name, immutableId }) => ({
                        name,
                        id: immutableId,
                    }));

                    this.dataset.push(...results);
                    this.filterListings(this.searchText);

                    return listings;
                })
                .catch(() => {
                    this.$error({ message: this.$t('notifications.error.campaignsPage.listings') });
                })
                .then((listings) => {
                    this.firstLoad = false;

                    if (listings.length && this.count < this.total) {
                        this.loadListings(++page);
                    } else {
                        this.loading = false;
                    }
                });
        },

        next() {
            if (this.validateBundle()) {
                this.confirmBundleDetails();
            } else {
                this.submitted = true;
            }
        },

        validateBundle() {
            return this.$refs.form.checkValidity() && Boolean(this.bundle.solutionRefs.length);
        },

        confirmBundleDetails() {
            this.$bus.$emit('PUSH_NESTED_MODAL', {
                component: CreateBundleConfirm,
                data: this.bundle,
                showRootClose: true,
            });
        },

        toggleCampaignSelect(listing) {
            if (this.isSelected(listing)) {
                this.removeCampaign(listing);
            } else {
                this.addCampaign(listing);
            }
        },

        isSelected(listing) {
            return Boolean(this.bundle.solutionRefs.filter(({ id }) => id === listing.id).length);
        },

        addCampaign(listing) {
            this.bundle.solutionRefs.push(listing);
        },

        removeCampaign(listing) {
            const campaignIndex = this.bundle.solutionRefs.findIndex(({ id }) => id === listing.id);

            this.bundle.solutionRefs.splice(campaignIndex, 1);
        },
    },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
    .create-bundle {
        border-radius: $border-radius;
        border: 1px solid $color-gray-30;
    }

    form {
        --input-margin-bottom: #{$spacing-200};
        border-bottom: 1px solid $color-gray-30;
        padding: $spacing-200 $spacing-200 0;
    }

    .title {
        display: flex;
        margin-top: $spacing-200;
        padding: 0 $spacing-200;
        font-weight: bold;
    }

    .loadingContainer {
        display: flex;
        margin-top: $spacing-200;
        padding: 0 $spacing-200;

        .totals {
            font-size: px-to-rem(14px);
            color: $color-gray;

            .numbers {
                color: $color-primary;
                font-weight: $font-weight-semibold;
            }
        }

        .spinner {
            --spinner-color: #{$color-primary};
            height: 1rem;
            width: 1rem;
            opacity: 0;
            transition: opacity 100ms linear;
            border-width: 1px;
            margin-right: $spacing-100;
        }

        .spinner.visible {
            opacity: 1;
        }
    }

    .placeholder {
        --placeholder-background: #{$color-white};

        .listings-placeholder {
            margin: $spacing-100 0;
        }
    }

    .placeholder, .listings-list {
        margin-top: $spacing-200;
        padding: $spacing-200;
        height: px-to-rem(360px);
    }

    .search-listings {
        --input-margin-bottom: 0;
        padding: $spacing-200 $spacing-200 0;
        display: flex;

        .input-field {
            margin-right: auto;
            max-width: px-to-rem(300px);

            @media($small) {
                max-width: 100%;
            }
        }
    }

    .bundles-selected {
        display: flex;
        align-items: center;
        margin: 0 $spacing-100;
    }

    .bundle-count {
        text-align: center;
        min-width: px-to-rem(24px);
        border-radius: $border-radius;
        background: $color-primary;
        color: $color-white;
        margin: 0 $spacing-100;
    }

    .listings-list {
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
    }

    .campaign {
        align-items: center;
        justify-content: space-between;
        display: flex;
        margin-bottom: $spacing-200;
    }

    .primary {
        margin-left: auto;
        min-width: px-to-rem(80px);
    }

    [dir=rtl] {
        .primary {
            margin-right: auto;
            margin-left: 0;
        }

        .search-listings {
            .input-field {
                margin-right: 0;
                margin-left: auto;
            }
        }

        .loadingContainer {

            .spinner {
                margin-right: 0;
                margin-left: $spacing-100;
            }
        }
    }
</style>
